import { useState, useEffect } from 'react'
import { RefreshCw, Radio, Trash2, Eye, EyeOff, CheckCircle2, AlertCircle, ChevronRight, X, Copy, Save, SlidersHorizontal } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import './LiveSettings.css'

// Live 2.0 tunables — written to app_settings (live_* keys); the coordinator reads them
// from the shared D1 each reconcile. Most keys/defaults mirror the coordinator's DEFAULT_SETTINGS;
// `youtube_offer_grace_seconds` is the exception — a watch-app tunable served via /api/config (not
// a coordinator setting), grouped here because it governs live playback.
const LIVE_TUNABLES = [
  { key: 'live_max_early_live_minutes', label: 'Max early live (min)', def: 15, hint: 'Hot HLS may show live this long before the scheduled start.' },
  { key: 'live_stream_drop_grace_minutes', label: 'Drop grace (min)', def: 2, hint: 'Short hold after a drop AT/AFTER the scheduled end, before ending.' },
  { key: 'live_max_interruption_minutes', label: 'Max interruption (min)', def: 20, hint: 'Auto-Stop ON: max hold as “interrupted” for a mid-window drop (before the scheduled end) before the event ENDS. A drop AT/AFTER the scheduled end uses “Drop grace” instead; Auto-Stop OFF uses “Abandoned live timeout”. During this hold the player shows the “Reconnecting…” transition (after “Stream offline grace”).' },
  { key: 'live_overrun_increment_minutes', label: 'Overrun step (min)', def: 15, hint: 'Extend the end in these steps while still live past schedule.' },
  { key: 'live_overrun_max_minutes', label: 'Overrun max (min)', def: 60, hint: 'Cap total overrun past the scheduled end.' },
  { key: 'live_grace_recheck_interval_seconds', label: 'Grace recheck (sec)', def: 15, hint: 'Poll cadence during drop-grace.' },
  { key: 'live_lifecycle_poll_interval_seconds', label: 'Live poll interval (sec)', def: 12, hint: 'SERVER-SIDE DETECTION: how often the coordinator polls Cloudflare Stream to detect stream state while an event is in-window / active. This is when the server LEARNS the truth — separate from how fast the client hears it (see In-window client poll).' },
  { key: 'live_prelive_poll_interval_seconds', label: 'Pre-live poll interval (sec)', def: 12, hint: 'SERVER-SIDE DETECTION: coordinator poll cadence in the pre-live window (awaiting the first hot HLS). Lower it to tighten go-live detection toward ~1–2s near start.' },
  { key: 'live_youtube_poll_interval_seconds', label: 'YouTube poll interval (sec)', def: 60, hint: 'How often the coordinator checks YouTube-live for in-window events with a YouTube ID. Higher = fewer YouTube API calls/day.' },
  { key: 'live_youtube_daily_quota_cap', label: 'YouTube daily quota cap (units)', def: 4000, hint: 'Hard cap on YouTube API units/day (shared 10k/day project). The coordinator stops YouTube polling past this and serves last-known status. Leaves headroom for the v1 stack.' },
  { key: 'live_youtube_dark_confirm_seconds', label: 'YouTube dark confirm (sec)', def: 120, hint: 'After YouTube stops reporting an event as live, keep showing it as YouTube-live this long before clearing — debounces brief YouTube API blips so the YouTube badge/offer doesn’t flicker.' },
  { key: 'live_youtube_offer_grace_seconds', label: 'YouTube offer grace (sec)', def: 25, hint: 'On a simulcast event whose CF (HLS) stream isn’t hot yet, how long the LIVE2 Watch button waits for HLS before offering a “Watch on YouTube” fallback. Higher = give HLS a longer head-start to win the CF-vs-YouTube race.' },
  { key: 'live_stream_offline_grace_seconds', label: 'Stream offline grace (sec)', def: 10, hint: 'How long the LIVE player holds the last live frame after the stream goes offline before switching to the thumbnail/“Reconnecting…” transition poster. Smooths brief reconnects. This is a DISPLAY hold only — the coordinator’s “Max interruption (min)” still governs when the event actually ENDS.' },
  { key: 'live_inwindow_poll_seconds', label: 'In-window client poll (sec)', def: 5, hint: 'CLIENT DELIVERY: how often the web client polls watch’s /api/v2/live/events — and the CF edge-cache TTL for that feed — WHILE any event is in-window (live / starting-soon). Lower = snappier LIVE-NOW / Stream-Ending / ended transitions; the CF edge absorbs the polls (≈1 origin rebuild per this many sec per POP), so it scales regardless of viewer count. This is the reliable channel on lossy wifi/LTE.' },
  { key: 'live_idle_poll_seconds', label: 'Idle client poll (sec)', def: 60, hint: 'CLIENT DELIVERY: client poll cadence + edge-cache TTL for the feed when NOTHING is in-window. Higher = cheaper when there’s nothing live to watch.' },
  { key: 'live_max_sse_per_input', label: 'Max SSE clients / input', def: 1500, hint: 'SSE is an OPTIONAL low-latency enhancement layered OVER the poll (toggle below; default off). This caps push connections held by one input DO; beyond it, clients ride the edge-cached poll — which is also the always-on floor even when SSE is on, so a dropped SSE never blinds a viewer. Scale + DoS backstop.' },
  { key: 'live_abandoned_live_timeout_minutes', label: 'Abandoned live timeout (min)', def: 30, hint: 'Auto-Stop OFF safety net: an “interrupted” event with no stream is force-ended after this long if you forget to press End (YouTube-style catch-all).' },
]

// YouTube hand-off prompts (text) — served to ALL watch apps via /api/config `live.*`. Use {title} for the
// event name. Shown when a viewer chooses to leave TenbandTV for a YouTube simulcast / a YouTube-only event.
const LIVE_MESSAGES = [
  { key: 'live_youtube_simulcast_message', label: 'YouTube simulcast prompt', def: 'A YouTube simulcast of {title} is available. Watching it will leave TenbandTV and open YouTube in a new tab.', hint: 'Shown when a viewer taps YouTube on an event that is ALSO live on TenbandTV. {title} = event name.' },
  { key: 'live_youtube_only_message', label: 'YouTube-only prompt', def: '{title} is streaming live on YouTube. Watching it will leave TenbandTV and open YouTube in a new tab.', hint: 'Shown for an event available ONLY on YouTube (no TenbandTV stream). {title} = event name.' },
  { key: 'live_youtube_ended_message', label: 'YouTube recording prompt', def: 'The recording of {title} is available on YouTube. Watching it will leave TenbandTV and open YouTube in a new tab.', hint: 'Shown for a recent-live (ended) YouTube-only event whose broadcast persists as a YouTube recording. {title} = event name.' },
]

// LIVE 2.0 — Live Events settings. First feature: manage the account-wide
// Cloudflare Stream webhook subscription (create/point/remove) that feeds the
// LIVE 2.0 coordinator, plus a live-input status panel. Server-side calls use
// vader's Stream:Edit token, so no credentials are handled in the browser.

export default function LiveSettings() {
  const { api } = useAuth()

  const [wh, setWh] = useState(null)
  const [target, setTarget] = useState('')
  const [inputs, setInputs] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [status, setStatus] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [selectedUid, setSelectedUid] = useState(null)
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [tunables, setTunables] = useState(null)
  const [messages, setMessages] = useState(null)
  // (web LIVE hero layout + immersive crop moved to Player Settings)
  const [sseEnabled, setSseEnabled] = useState(false) // optional SSE push over the adaptive poll (default off)
  const [showChannelOnCards, setShowChannelOnCards] = useState(false) // show the channel name/logo on live cards + info modals (watch, via /api/config; default off)
  const [savingTunables, setSavingTunables] = useState(false)
  const [yt, setYt] = useState(null) // YouTube OAuth connection status
  const [backfill, setBackfill] = useState(null) // backfill/adopt report
  const [discover, setDiscover] = useState(null) // discover-from-youtube import report

  useEffect(() => { loadWebhook(); loadTunables(); loadYt() }, [])

  async function loadYt() {
    try { setYt(await api('/youtube/oauth/status')) } catch (e) { setYt({ connected: false, error: e.message }) }
  }
  async function connectYouTube() {
    setBusy('yt'); setStatus('')
    try {
      const d = await api('/youtube/oauth/connect')
      if (d?.url) {
        window.open(d.url, '_blank', 'noopener')
        setStatus('Opened Google consent in a new tab — sign in as the channel Owner and select the TenbandTV brand, then click Refresh.')
      } else setStatus(`Error: ${d?.error || 'no url'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setBusy('')
  }
  async function disconnectYouTube() {
    if (!window.confirm('Disconnect YouTube? Simulcast provisioning will stop working until reconnected.')) return
    setBusy('yt'); setStatus('')
    try { await api('/youtube/oauth/disconnect', { method: 'POST' }); await loadYt(); setStatus('YouTube disconnected.') }
    catch (e) { setStatus(`Error: ${e.message}`) }
    setBusy('')
  }
  // Backfill/adopt: scan live events that already carry a youtube_id and preview which are real, owned
  // liveBroadcasts that can be adopted into the new simulcast format. Dry-run first, then apply.
  async function previewBackfill() {
    setBusy('backfill'); setStatus('')
    try {
      const r = await api('/youtube/simulcast/backfill', { method: 'POST', body: JSON.stringify({ dryRun: true }) })
      if (r?.ok) { setBackfill(r); setStatus(`Preview: ${r.adoptable} of ${r.scanned} live event(s) can be adopted.`) }
      else setStatus(`Backfill: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setBusy('')
  }
  async function applyBackfill() {
    const n = backfill?.adoptable || 0
    if (!window.confirm(`Adopt ${n} existing YouTube broadcast(s) as managed simulcasts? They stay owned by YouTube (Remove will unlink, not delete).`)) return
    setBusy('backfill'); setStatus('')
    try {
      const r = await api('/youtube/simulcast/backfill', { method: 'POST', body: JSON.stringify({ dryRun: false }) })
      if (r?.ok) { setBackfill(r); setStatus(`✅ Adopted ${r.applied} simulcast(s).`) }
      else setStatus(`Backfill: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setBusy('')
  }
  // Discover: pull TBTV-channel broadcasts created directly on YouTube and import them as draft live events.
  async function previewDiscover() {
    setBusy('discover'); setStatus('')
    try {
      const r = await api('/youtube/simulcast/discover', { method: 'POST', body: JSON.stringify({ dryRun: true }) })
      if (r?.ok) { setDiscover(r); setStatus(`Discover: ${r.importable} new TBTV broadcast(s) not yet in the catalog (of ${r.found} found).`) }
      else setStatus(`Discover: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setBusy('')
  }
  async function applyDiscover() {
    const n = discover?.importable || 0
    if (!window.confirm(`Import ${n} YouTube broadcast(s) as draft live events + adopted simulcasts? They stay owned by YouTube (Remove unlinks, not deletes). Assign a CF input afterward if you want TBTV HLS too.`)) return
    setBusy('discover'); setStatus('')
    try {
      const r = await api('/youtube/simulcast/discover', { method: 'POST', body: JSON.stringify({ dryRun: false }) })
      if (r?.ok) { setDiscover(r); setStatus(`✅ Imported ${r.imported} live event(s).`) }
      else setStatus(`Discover: ${r?.message || r?.error || 'failed'}`)
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setBusy('')
  }

  async function loadTunables() {
    try {
      const d = await api('/settings?prefix=live_')
      const s = d.settings || {}
      const t = {}
      for (const f of LIVE_TUNABLES) t[f.key] = s[f.key] != null ? Number(s[f.key]) : f.def
      setTunables(t)
      const m = {}
      for (const f of LIVE_MESSAGES) m[f.key] = s[f.key] != null ? String(s[f.key]) : f.def
      setMessages(m)
      setSseEnabled(String(s.live_sse_enabled) === '1')
      setShowChannelOnCards(String(s.live_show_channel) === 'true')
    } catch (e) { setStatus(`Error loading tunables: ${e.message}`) }
  }

  async function saveTunables() {
    setSavingTunables(true); setStatus('')
    try {
      const settings = {}
      for (const f of LIVE_TUNABLES) settings[f.key] = String(tunables[f.key])
      for (const f of LIVE_MESSAGES) settings[f.key] = String((messages?.[f.key] ?? f.def)).trim() || f.def
      // (live_hero_layout / live_immersive_crop moved to Player Settings — not written here.)
      settings.live_sse_enabled = sseEnabled ? '1' : '0'
      settings.live_show_channel = showChannelOnCards ? 'true' : 'false'
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings }) })
      setStatus('Live settings saved — the coordinator applies them on its next reconcile (~1 min).')
    } catch (e) { setStatus(`Error: ${e.message}`) }
    setSavingTunables(false)
  }

  async function loadWebhook() {
    setLoading(true)
    try {
      const d = await api('/live/webhook')
      setWh(d)
      setTarget(d.targetUrl || '')
    } catch (e) {
      setStatus(`Error loading webhook: ${e.message}`)
    }
    setLoading(false)
  }

  async function pointWebhook() {
    setBusy('set'); setStatus('')
    try {
      const d = await api('/live/webhook', {
        method: 'PUT',
        body: JSON.stringify({ notificationUrl: target }),
      })
      if (d.success) {
        setStatus('Webhook subscribed ✓ — copy the signing secret to the coordinator (CF_WEBHOOK_SECRET) for signature verification.')
        setShowSecret(true)
        await loadWebhook()
      } else {
        setStatus(`CF error: ${d.error}${d.cfErrors ? ' — ' + JSON.stringify(d.cfErrors) : ''}`)
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setBusy('')
  }

  async function removeWebhook() {
    if (!window.confirm('Remove the account-wide Cloudflare Stream webhook subscription? Live events will stop delivering webhooks until re-added.')) return
    setBusy('del'); setStatus('')
    try {
      const d = await api('/live/webhook', { method: 'DELETE' })
      setStatus(d.success ? 'Webhook subscription removed.' : `CF error: ${JSON.stringify(d.cfErrors)}`)
      await loadWebhook()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setBusy('')
  }

  async function loadInputs() {
    setBusy('inputs'); setStatus('')
    try {
      const d = await api('/live/inputs/status')
      setInputs(d)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setBusy('')
  }

  async function selectInput(uid) {
    if (selectedUid === uid) { setSelectedUid(null); setSummary(null); return }
    setSelectedUid(uid); setSummary(null); setSummaryLoading(true)
    try {
      const d = await api(`/live/inputs/${uid}/summary`)
      setSummary(d)
    } catch (e) {
      setStatus(`Error loading summary: ${e.message}`)
      setSelectedUid(null)
    }
    setSummaryLoading(false)
  }

  // Note: we do NOT block the whole page on `loading` — that's the Cloudflare webhook lookup, a slow
  // external API call. The page (incl. the fast tunables) renders immediately; the webhook card shows its
  // own inline "Checking…" state.
  const sub = wh && wh.subscription
  const configured = wh && wh.configured
  const cfReady = wh && wh.cfReady

  return (
    <div className="live-settings">
      <div className="live-settings-header">
        <h1>Live Events</h1>
        <p className="live-settings-subtitle">
          LIVE 2.0 event infrastructure — webhook wiring and live-input status.
        </p>
      </div>

      {status && (
        <div className={`live-settings-status ${status.startsWith('Error') || status.startsWith('CF error') ? 'error' : 'success'}`}>
          {status}
        </div>
      )}

      {/* ---- CF Stream webhook subscription ---- */}
      <div className="live-settings-card">
        <div className="live-settings-card-head">
          <h3><Radio size={18} /> Cloudflare Stream Webhook</h3>
          <span className={`live-badge ${loading ? 'off' : configured ? 'ok' : 'off'}`}>
            {loading ? <><RefreshCw size={14} className="spin" /> Checking…</>
              : configured ? <><CheckCircle2 size={14} /> Subscribed</>
              : <><AlertCircle size={14} /> Not set</>}
          </span>
        </div>
        <p className="live-settings-hint">
          One account-wide subscription. Cloudflare POSTs live-input state changes (connected /
          disconnected / recording ready) to this URL — the LIVE 2.0 coordinator's ingest endpoint.
        </p>

        {!loading && !cfReady && (
          <div className="live-settings-status error">
            Cloudflare Stream credentials (CF_API_TOKEN / CF_ACCOUNT_ID) are not configured on vader.
          </div>
        )}

        <div className="live-settings-field">
          <label>Notification URL (coordinator ingest)</label>
          <input
            type="text"
            value={target}
            onChange={e => setTarget(e.target.value)}
            spellCheck={false}
            placeholder="https://tbtv-live-coordinator.todd-dff.workers.dev/webhooks/cf"
          />
        </div>

        {configured && (
          <div className="live-settings-kv">
            <div><span>Current URL</span><code>{sub.notificationUrl}</code></div>
            {sub.modified && <div><span>Modified</span><code>{sub.modified}</code></div>}
            {sub.secret && (
              <div>
                <span>Signing secret</span>
                <code className="secret">
                  {showSecret ? sub.secret : '•'.repeat(20)}
                </code>
                <button className="live-icon-btn" onClick={() => setShowSecret(s => !s)} title={showSecret ? 'Hide' : 'Reveal'}>
                  {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            )}
            {!wh.matchesTarget && (
              <div className="live-settings-warn">Current URL differs from the target above — click “Point webhook at coordinator” to update.</div>
            )}
          </div>
        )}

        <div className="live-settings-actions">
          <button className="btn-primary" disabled={loading || !cfReady || busy === 'set'} onClick={pointWebhook}>
            {busy === 'set' ? 'Saving…' : (configured ? 'Update webhook' : 'Point webhook at coordinator')}
          </button>
          <button className="btn-ghost" disabled={loading || busy === 'refresh'} onClick={loadWebhook}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh
          </button>
          {configured && (
            <button className="btn-danger" disabled={busy === 'del'} onClick={removeWebhook}>
              <Trash2 size={15} /> Remove
            </button>
          )}
        </div>
      </div>

      {/* ---- YouTube simulcast (OAuth) ---- */}
      <div className="live-settings-card">
        <div className="live-settings-card-head">
          <h3><Radio size={18} /> YouTube Simulcast</h3>
          <span className={`live-badge ${yt?.connected ? 'ok' : 'off'}`}>
            {yt == null ? <><RefreshCw size={14} className="spin" /> Checking…</>
              : yt.connected ? <><CheckCircle2 size={14} /> Connected</>
              : <><AlertCircle size={14} /> Not connected</>}
          </span>
        </div>
        <p className="live-settings-hint">
          Connect the <strong>TenbandTV</strong> YouTube channel (as an <strong>Owner</strong>) to enable
          "Create YouTube Simulcast" on live events. Consent must resolve to the TenbandTV brand — any other
          channel is rejected. OAuth + the CF restream are handled inside vader.
        </p>
        {yt?.connected && (
          <div className="live-settings-kv">
            <div><span>Channel</span><code>{yt.accountLabel || '—'}</code></div>
            {yt.connectedAt && <div><span>Connected</span><code>{yt.connectedAt}</code></div>}
          </div>
        )}
        <div className="live-settings-actions">
          <button className="btn-primary" disabled={busy === 'yt'} onClick={connectYouTube}>
            {busy === 'yt' ? 'Working…' : (yt?.connected ? 'Reconnect' : 'Connect YouTube')}
          </button>
          <button className="btn-ghost" disabled={busy === 'yt'} onClick={loadYt}>
            <RefreshCw size={15} className={yt == null ? 'spin' : ''} /> Refresh
          </button>
          {yt?.connected && (
            <button className="btn-danger" disabled={busy === 'yt'} onClick={disconnectYouTube}>
              <Trash2 size={15} /> Disconnect
            </button>
          )}
        </div>

        {/* Backfill/adopt existing scheduled broadcasts into the simulcast format */}
        {yt?.connected && (
          <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 14, paddingTop: 12 }}>
            <h4 style={{ margin: '0 0 4px', fontSize: 14 }}>Import existing scheduled broadcasts</h4>
            <p className="live-settings-hint" style={{ marginTop: 0 }}>
              Live events that already carry a YouTube ID (e.g. from archive sync) can be adopted as managed
              simulcasts. Only IDs that are real live broadcasts <strong>owned by the connected channel</strong>
              are adoptable — regular videos and other channels' IDs are skipped. Adopted broadcasts stay owned
              by YouTube: <strong>Remove unlinks, it does not delete</strong>. Preview first.
            </p>
            <div className="live-settings-actions">
              <button className="btn-ghost" disabled={busy === 'backfill'} onClick={previewBackfill}>
                <RefreshCw size={15} className={busy === 'backfill' ? 'spin' : ''} /> Preview
              </button>
              {backfill && backfill.adoptable > 0 && (
                <button className="btn-primary" disabled={busy === 'backfill'} onClick={applyBackfill}>
                  Adopt {backfill.adoptable} broadcast{backfill.adoptable !== 1 ? 's' : ''}
                </button>
              )}
            </div>
            {backfill && (
              <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, padding: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                  Scanned {backfill.scanned} · adoptable {backfill.adoptable}{backfill.applied ? ` · applied ${backfill.applied}` : ''} {backfill.dryRun ? '(preview)' : '(applied)'}
                </div>
                {(backfill.report || []).map((it) => (
                  <div key={it.tbtvId + it.youtubeId} style={{ fontSize: 12, padding: '4px 0', borderTop: '1px solid var(--color-border)' }}>
                    <span style={{ fontWeight: 600 }}>{it.adoptable ? '✅' : '—'} {it.title}</span>
                    <span style={{ color: 'var(--color-text-muted)' }}> · {it.youtubeId} · {it.action}{it.lifeCycle ? ` · ${it.lifeCycle}/${it.privacy}` : ''}</span>
                    {it.note && <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{it.note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Discover broadcasts created directly on YouTube (not yet in the catalog) and import them */}
        {yt?.connected && (
          <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 14, paddingTop: 12 }}>
            <h4 style={{ margin: '0 0 4px', fontSize: 14 }}>Import broadcasts created on YouTube</h4>
            <p className="live-settings-hint" style={{ marginTop: 0 }}>
              Pulls the TBTV channel's own <strong>upcoming/live</strong> broadcasts straight from YouTube (via the
              OAuth connection — works even with archive sync off). Any not already in the catalog can be imported
              as <strong>draft live events + adopted simulcasts</strong>. Assign a CF input afterward if you also
              want the TBTV HLS feed. Remove unlinks, it does not delete. Preview first.
            </p>
            <div className="live-settings-actions">
              <button className="btn-ghost" disabled={busy === 'discover'} onClick={previewDiscover}>
                <RefreshCw size={15} className={busy === 'discover' ? 'spin' : ''} /> Preview
              </button>
              {discover && discover.importable > 0 && (
                <button className="btn-primary" disabled={busy === 'discover'} onClick={applyDiscover}>
                  Import {discover.importable} broadcast{discover.importable !== 1 ? 's' : ''}
                </button>
              )}
            </div>
            {discover && (
              <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, padding: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6 }}>
                  Found {discover.found} · importable {discover.importable}{discover.imported ? ` · imported ${discover.imported}` : ''} {discover.dryRun ? '(preview)' : '(applied)'}
                </div>
                {(discover.report || []).map((it) => (
                  <div key={it.broadcastId} style={{ fontSize: 12, padding: '4px 0', borderTop: '1px solid var(--color-border)' }}>
                    <span style={{ fontWeight: 600 }}>{it.importable ? '✅' : '—'} {it.title}</span>
                    <span style={{ color: 'var(--color-text-muted)' }}> · {it.broadcastId} · {it.action}{it.lifeCycle ? ` · ${it.lifeCycle}/${it.privacy}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---- Live 2.0 tunables ---- */}
      <div className="live-settings-card">
        <div className="live-settings-card-head">
          <h3><SlidersHorizontal size={18} /> Live Settings</h3>
        </div>
        <p className="live-settings-hint">
          Timing tunables for the LIVE 2.0 state machine. Applied by the coordinator on its next
          reconcile (~1 min after saving).
        </p>
        {tunables && (
          <>
            {/* Client delivery model — how the web client learns live state */}
            <div className="live-settings-field" style={{ marginBottom: 12 }}>
              <label>Live delivery: SSE push</label>
              <select
                value={sseEnabled ? 'on' : 'off'}
                onChange={e => setSseEnabled(e.target.value === 'on')}
                style={{ width: '100%', boxSizing: 'border-box' }}
              >
                <option value="off">Off — adaptive edge-cached poll only (default, most reliable)</option>
                <option value="on">On — layer SSE real-time push over the poll floor</option>
              </select>
              <span className="live-tunable-hint">
                The web client always learns live state by polling watch’s edge-cached feed at the “In-window / Idle
                client poll” cadence above — this is the scalable, lossy-network-resilient base and works on its own.
                Turning SSE On adds an optional sub-second push layer for engaged player pages; the poll stays the
                always-on floor beneath it, so a dropped SSE never blinds a viewer. Off is recommended while we
                evaluate the poll-only model.
              </span>
            </div>

            {/* Web display: show the channel a live event plays on (name + logo) on cards + info modals */}
            <div className="live-settings-field" style={{ marginBottom: 12 }}>
              <label>Show channel on live cards &amp; info</label>
              <select
                value={showChannelOnCards ? 'on' : 'off'}
                onChange={e => setShowChannelOnCards(e.target.value === 'on')}
                style={{ width: '100%', boxSizing: 'border-box' }}
              >
                <option value="off">Off — don’t show the channel (default)</option>
                <option value="on">On — show the channel name + logo the live plays on</option>
              </select>
              <span className="live-tunable-hint">
                When On, live event cards and info modals on the Splash and Live pages show the name and logo of the
                channel the event is configured to play on. A web display setting served to the apps via /api/config
                (≈5-min propagation) — it does not affect the coordinator.
              </span>
            </div>

            <div className="live-tunables-grid">
              {LIVE_TUNABLES.map(f => (
                <div key={f.key} className="live-settings-field" title={f.hint}>
                  <label>{f.label}</label>
                  <input
                    type="number" min={0}
                    value={tunables[f.key]}
                    onChange={e => setTunables(t => ({ ...t, [f.key]: Number(e.target.value) }))}
                  />
                  <span className="live-tunable-hint">{f.hint}</span>
                </div>
              ))}
            </div>

            {/* (Web LIVE hero layout + immersive crop moved to Settings → Player Settings.) */}

            {/* YouTube hand-off prompts (served to all watch apps via /api/config; {title} = event name) */}
            {messages && (
              <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 12, paddingTop: 12 }}>
                <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>YouTube hand-off prompts</h4>
                {LIVE_MESSAGES.map(f => (
                  <div key={f.key} className="live-settings-field" style={{ marginBottom: 10 }}>
                    <label>{f.label}</label>
                    <textarea
                      rows={3}
                      value={messages[f.key]}
                      onChange={e => setMessages(m => ({ ...m, [f.key]: e.target.value }))}
                      style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    <span className="live-tunable-hint">{f.hint}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="live-settings-actions">
              <button className="btn-primary" disabled={savingTunables} onClick={saveTunables}>
                <Save size={15} /> {savingTunables ? 'Saving…' : 'Save live settings'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ---- Live input status ---- */}
      <div className="live-settings-card">
        <div className="live-settings-card-head">
          <h3><Radio size={18} /> Live Input Status</h3>
          <button className="btn-ghost" disabled={busy === 'inputs'} onClick={loadInputs}>
            <RefreshCw size={15} className={busy === 'inputs' ? 'spin' : ''} /> {inputs ? 'Refresh' : 'Load'}
          </button>
        </div>
        <p className="live-settings-hint">
          Current ingest state of every Cloudflare live input. <strong>connected</strong> = an encoder
          is streaming right now.
        </p>

        {inputs && inputs.inputs && (
          <>
            <div className="live-settings-summary">
              {inputs.connected} of {inputs.count} inputs connected
            </div>
            <div className="live-inputs-table">
              {inputs.inputs.map(i => (
                <div
                  key={i.uid}
                  className={`live-input-row clickable state-${i.state} ${selectedUid === i.uid ? 'selected' : ''}`}
                  onClick={() => selectInput(i.uid)}
                  role="button"
                  tabIndex={0}
                >
                  <span className={`live-dot ${i.state}`} />
                  <span className="live-input-name">{i.name || '(unnamed)'}</span>
                  <span className="live-input-state">{i.state}</span>
                  <code className="live-input-uid">{i.uid}</code>
                  <ChevronRight size={15} className={`live-row-caret ${selectedUid === i.uid ? 'open' : ''}`} />
                </div>
              ))}
            </div>
          </>
        )}
        {inputs && inputs.error && <div className="live-settings-status error">{inputs.error}</div>}

        {selectedUid && (
          <div className="live-modal-overlay" onClick={() => { setSelectedUid(null); setSummary(null) }}>
          <div className="live-modal" onClick={e => e.stopPropagation()}>
            <div className="live-summary-head">
              <h4>Input summary</h4>
              <button className="live-icon-btn" onClick={() => { setSelectedUid(null); setSummary(null) }} title="Close">
                <X size={16} />
              </button>
            </div>

            {summaryLoading && <p className="live-settings-hint">Loading summary…</p>}

            {summary && (
              <>
                {summary.error && <div className="live-settings-status error">{summary.error}</div>}

                {summary.input && (
                  <div className="live-summary-block">
                    <div className="live-summary-title">
                      {summary.input.name || '(unnamed)'} <code>{summary.uid}</code>
                    </div>
                    {summary.input.status && summary.input.status.current ? (
                      <div className="live-settings-kv">
                        <div><span>State</span>
                          <code className={summary.input.status.current.state === 'connected' ? 'live-ok' : ''}>
                            {summary.input.status.current.state}
                          </code>
                        </div>
                        <div><span>Reason</span><code>{summary.input.status.current.reason || '—'}</code></div>
                        <div><span>Ingest</span><code>{summary.input.status.current.ingestProtocol || '—'}</code></div>
                        <div><span>Since</span><code>{summary.input.status.current.statusEnteredAt || '—'}</code></div>
                        <div><span>Last seen</span><code>{summary.input.status.current.statusLastSeen || '—'}</code></div>
                        <div><span>Recording</span><code>{summary.input.recording?.mode || '—'}</code></div>
                      </div>
                    ) : (
                      <p className="live-settings-hint">No ingest status — this input has never received a stream.</p>
                    )}
                  </div>
                )}

                {(summary.liveVideo || summary.inputHls) && (
                  <div className="live-summary-block">
                    <div className="live-summary-title">Live playback</div>
                    <div className="live-settings-kv">
                      {summary.liveVideo && (
                        <>
                          <div><span>Recording</span><code>{summary.liveVideo.state}</code></div>
                          <div><span>Duration</span><code>{summary.liveVideo.duration >= 0 ? `${summary.liveVideo.duration}s` : 'live (—)'}</code></div>
                        </>
                      )}
                      {(summary.liveVideo?.hls || summary.inputHls) && (
                        <div>
                          <span>HLS</span>
                          <code>{summary.liveVideo?.hls || summary.inputHls}</code>
                          <button className="live-icon-btn" title="Copy"
                            onClick={() => navigator.clipboard.writeText(summary.liveVideo?.hls || summary.inputHls)}>
                            <Copy size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="live-summary-block">
                  <div className="live-summary-title">
                    Webhook events
                    <span className="live-count">{summary.webhook?.eventCount ?? 0}</span>
                  </div>
                  <p className="live-settings-hint">
                    Payloads Cloudflare has POSTed for this input to the coordinator
                    (<code>{summary.webhook?.notificationUrl}</code>).
                  </p>
                  {summary.webhook?.error && <div className="live-settings-status error">{summary.webhook.error}</div>}
                  {(!summary.webhook?.events || summary.webhook.events.length === 0) ? (
                    <p className="live-settings-hint">
                      No webhook events captured yet for this input. Cloudflare fires on state changes
                      (connect / disconnect / recording ready) — stop or start the encoder to generate one.
                    </p>
                  ) : (
                    <div className="live-events-list">
                      {summary.webhook.events.map((ev, idx) => (
                        <details key={idx} className="live-event">
                          <summary>
                            <span className="live-event-time">{ev.receivedAt}</span>
                            {ev.parsed?.eventType && <span className="live-event-type">{ev.parsed.eventType}</span>}
                          </summary>
                          <pre>{JSON.stringify(ev.parsed ?? ev.body, null, 2)}</pre>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  )
}
